import * as PrismaTypes from '@prisma/client';
import * as AppTypes from './shared';
import { SearchParams } from './search-params';
import * as UserTypes from './user-types';
import { OpenGraph } from 'next/dist/lib/metadata/types/opengraph-types';
import { SubscriptionForSchema } from './metadata-types';
import { Offer } from 'schema-dts';
import { BranchForSubscription, BranchWithParsedCases } from './branch-types';
import { Prisma } from '@prisma/client';
import { PaymentEvent, RefundEvent, YooKassaEvent } from './yookassa';
import {
  PaymentRedisRecordData,
  PersonalDataForReceipt
} from './payment-types';
import { UsedPromoCodeInput } from './promo-code-types';

export const isString = (text: unknown): text is string => {
  return typeof text === 'string' || text instanceof String;
};

export const isNumber = (text: unknown): text is number => {
  return typeof text === 'number' || text instanceof Number;
};

export const isBoolean = (value: unknown): value is boolean => {
  return typeof value === 'boolean' || value instanceof Boolean;
};

export const isDifficulty = (
  value: unknown
): value is PrismaTypes.Difficulty => {
  return Object.values(PrismaTypes.Difficulty).includes(
    value as PrismaTypes.Difficulty
  );
};

export const isPlacement = (value: unknown): value is PrismaTypes.Placement =>
  Object.values(PrismaTypes.Placement).includes(value as PrismaTypes.Placement);

export const isCurrency = (value: unknown): value is PrismaTypes.Currency =>
  Object.values(PrismaTypes.Currency).includes(value as PrismaTypes.Currency);

export const isPaymentStatus = (
  value: unknown
): value is PrismaTypes.PaymentStatus =>
  Object.values(PrismaTypes.PaymentStatus).includes(
    value as PrismaTypes.PaymentStatus
  );

export const isPaymentAction = (
  value: unknown
): value is PrismaTypes.PaymentAction =>
  Object.values(PrismaTypes.PaymentAction).includes(
    value as PrismaTypes.PaymentAction
  );

export const isActivationSource = (
  value: unknown
): value is PrismaTypes.ActivationSource =>
  Object.values(PrismaTypes.ActivationSource).includes(
    value as PrismaTypes.ActivationSource
  );

export const isValidDate = (value: unknown): value is Date =>
  value instanceof Date && !isNaN(value.getTime());

export const isISOString = (value: unknown): value is string => {
  if (!isString(value)) return false;
  const date = new Date(value);
  return isValidDate(date);
};

export const isISOStringOrNull = (value: unknown): value is string => {
  if (value === null) return true;

  if (!isString(value)) return false;
  const date = new Date(value);

  return isValidDate(date);
};

export const isNullableDate = (value: unknown): value is Date | null => {
  return value === null || (value instanceof Date && !isNaN(value.getTime()));
};

export const isAccessLevel = (
  value: unknown
): value is PrismaTypes.AccessLevel => {
  return Object.values(PrismaTypes.AccessLevel).includes(
    value as PrismaTypes.AccessLevel
  );
};

export const isSubscriptionDetails = (
  obj: unknown
): obj is AppTypes.SubscriptionDetails => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }
  return (
    'branchId' in obj &&
    isNumber(obj.branchId) &&
    'branchSlug' in obj &&
    isString(obj.branchSlug) &&
    'accessLevel' in obj &&
    isAccessLevel(obj.accessLevel) &&
    'expiresAt' in obj &&
    isISOString(obj.expiresAt)
  );
};

export const isSubscriptionDetailsArray = (
  obj: unknown
): obj is AppTypes.SubscriptionDetails[] => {
  return Array.isArray(obj) && obj.every(item => isSubscriptionDetails(item));
};

export const isSubscription = (obj: unknown): obj is AppTypes.Subscription => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  return (
    'planId' in obj &&
    isNumber(obj.planId) &&
    'name' in obj &&
    isString(obj.name) &&
    'allIn' in obj &&
    isBoolean(obj.allIn) &&
    'subscriptionDetails' in obj &&
    isSubscriptionDetailsArray(obj.subscriptionDetails)
  );
};

export const isThemeMode = (value: unknown): value is AppTypes.ThemeMode => {
  return value === 'light' || value === 'dark';
};

export const isDbUserPersonalData = (
  obj: unknown
): obj is AppTypes.DbUserPersonalData => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  const necessaryFields = 'username' in obj && isString(obj.username);

  return (
    necessaryFields &&
    // Также проверяем, что если поле avatar существует, то оно либо undefined, либо строка
    ('email' in obj ? obj.email === undefined || isString(obj.email) : true) &&
    ('avatar' in obj ? obj.avatar === undefined || isString(obj.avatar) : true)
  );
};

export const isPublicUserPersonalData = (
  obj: unknown
): obj is AppTypes.PublicUserPersonalData => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  const necessaryFields = 'username' in obj;

  return (
    necessaryFields &&
    // Также проверяем, что если поле avatar существует, то оно либо undefined, либо строка
    ('avatar' in obj ? obj.avatar === undefined || isString(obj.avatar) : true)
  );
};

export const isUserPreferences = (
  obj: unknown
): obj is AppTypes.UserPreferences => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  return (
    'showAvatar' in obj &&
    isBoolean(obj.showAvatar) &&
    'theme' in obj &&
    isThemeMode(obj.theme)
  );
};

export const isUserClient = (obj: unknown): obj is AppTypes.UserClient => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  return (
    'personalData' in obj &&
    isPublicUserPersonalData(obj.personalData) &&
    'preferences' in obj &&
    isUserPreferences(obj.preferences) &&
    'status' in obj &&
    isSubscriptionStatus(obj.status) &&
    'access' in obj &&
    isSubscriptionDetailsArray(obj.access)
  );
};

export const isSearchParams = (obj: unknown): obj is SearchParams => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  const { query, levels, page } = obj as SearchParams;

  return (
    (query === undefined || typeof query === 'string') &&
    (levels === undefined ||
      (Array.isArray(levels) && levels.every(l => typeof l === 'string'))) &&
    (page === undefined || typeof page === 'string')
  );
};

export const isVKTokens = (obj: unknown): obj is AppTypes.VKTokens => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  return (
    'accessToken' in obj &&
    isString(obj.accessToken) &&
    'refreshToken' in obj &&
    isString(obj.refreshToken)
  );
};

// export const isVKData = (obj: unknown): obj is AppTypes.VKData => {
//   if (typeof obj !== 'object' || obj === null) {
//     return false;
//   }

//   const vkTokens = isVKTokens(obj);

//   const necessaryFields =
//     vkTokens &&
//     'refreshTokenExpiresAt' in obj &&
//     isNumber(obj.refreshTokenExpiresAt) &&
//     'deviceId' in obj &&
//     isString(obj.deviceId) &&
//     'vkId' in obj &&
//     isString(obj.vkId);

//   return (
//     necessaryFields &&
//     ('avatar' in obj ? obj.avatar === undefined || isString(obj.avatar) : true)
//   );
// };

export const isRegistrationData = (
  obj: unknown
): obj is AppTypes.RegistrationData => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  const necessaryFields =
    'username' in obj &&
    isString(obj.username) &&
    'vkId' in obj &&
    isString(obj.vkId);

  return (
    necessaryFields &&
    // Также проверяем, что если поле avatar существует, то оно либо undefined, либо строка
    ('email' in obj ? obj.email === undefined || isString(obj.email) : true) &&
    ('avatar' in obj ? obj.avatar === undefined || isString(obj.avatar) : true)
  );
};

export const isSignUpSessionForClient = (
  obj: unknown
): obj is AppTypes.SignUpSessionForClient => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }
  return (
    'userId' in obj &&
    isString(obj.userId) &&
    'registrationData' in obj &&
    isRegistrationData(obj.registrationData) &&
    'subscription' in obj &&
    isSubscription(obj.subscription)
  );
};

export const isSignUpSessionForRedisIncomingData = (
  obj: unknown
): obj is AppTypes.SignUpSessionForRedisIncomingData => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }
  return (
    'userId' in obj &&
    isString(obj.userId) &&
    'personalData' in obj &&
    isDbUserPersonalData(obj.personalData) &&
    'vkId' in obj &&
    isString(obj.vkId) &&
    'subscription' in obj &&
    isSubscription(obj.subscription)
  );
};

export const isSignUpSessionForRedis = (
  obj: unknown
): obj is AppTypes.SignUpSessionForRedis => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }
  return (
    'userId' in obj &&
    isString(obj.userId) &&
    'encryptedRegistrationData' in obj &&
    isString(obj.encryptedRegistrationData) &&
    'hmac' in obj &&
    isString(obj.hmac) &&
    'subscription' in obj &&
    isSubscription(obj.subscription)
  );
};

export const isSubscriptionStatus = (
  value: unknown
): value is PrismaTypes.SubscriptionStatus => {
  return Object.values(PrismaTypes.SubscriptionStatus).includes(
    value as PrismaTypes.SubscriptionStatus
  );
};

export const isUserDataForSessionsDB = (
  obj: unknown
): obj is AppTypes.UserDataForSessionsDB => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  const encryptedData =
    'encryptedPersonalData' in obj &&
    isString(obj.encryptedPersonalData) &&
    'hmac' in obj;

  const openData =
    'preferences' in obj &&
    isUserPreferences(obj.preferences) &&
    'subscriptionStatus' in obj &&
    isSubscriptionStatus(obj.subscriptionStatus) &&
    'planId' in obj &&
    isNumber(obj.planId);

  return 'id' in obj && encryptedData && openData;
};

export const isSessionRedis = (obj: unknown): obj is AppTypes.SessionRedis => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  return (
    'userId' in obj &&
    isString(obj.userId) &&
    'pgSessionId' in obj &&
    isString(obj.pgSessionId) &&
    'encryptedPersonalData' in obj &&
    isString(obj.encryptedPersonalData) &&
    'hmac' in obj &&
    isString(obj.hmac) &&
    'subscriptionStatus' in obj &&
    isSubscriptionStatus(obj.subscriptionStatus) &&
    'subscriptionExpiresAt' in obj &&
    isISOStringOrNull(obj.subscriptionExpiresAt) &&
    'subscription' in obj &&
    isSubscription(obj.subscription)
  );
};

export const isSessionClient = (
  obj: unknown
): obj is AppTypes.SessionClient => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  const sessionClientFromUserServiceFields =
    isSessionClientFromUserService(obj);

  return (
    sessionClientFromUserServiceFields &&
    'pgSessionId' in obj &&
    isString(obj.pgSessionId)
  );
};

export const isSessionClientFromUserService = (
  obj: unknown
): obj is AppTypes.SessionClientFromUserService => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  return (
    'userId' in obj &&
    isString(obj.userId) &&
    'personalData' in obj &&
    isPublicUserPersonalData(obj.personalData) &&
    'preferences' in obj &&
    isUserPreferences(obj.preferences) &&
    'subscriptionStatus' in obj &&
    isSubscriptionStatus(obj.subscriptionStatus) &&
    'subscriptionExpiresAt' in obj &&
    isISOStringOrNull(obj.subscriptionExpiresAt) &&
    'subscription' in obj &&
    isSubscription(obj.subscription)
  );
};

export const isSessionIdAndUserClient = (
  obj: unknown
): obj is AppTypes.SessionIdAndUserClient => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  return (
    'sessionId' in obj &&
    isString(obj.sessionId) &&
    'user' in obj &&
    isUserClient(obj.user)
  );
};

export const isConfigUrlData = (
  obj: unknown
): obj is AppTypes.ConfigUrlData => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  return (
    'id' in obj &&
    isNumber(obj.id) &&
    'difficulty' in obj &&
    isDifficulty(obj.difficulty) &&
    'topicSlug' in obj &&
    isString(obj.topicSlug)
  );
};

export const isPaginationData = (
  obj: unknown
): obj is AppTypes.PaginationData => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  return (
    'total' in obj &&
    isNumber(obj.total) &&
    'topicId' in obj &&
    isNumber(obj.topicId) &&
    'searchParams' in obj &&
    isSearchParams(obj.searchParams)
  );
};

export const isCreateUserArgs = (
  obj: unknown
): obj is UserTypes.CreateUserArgs => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  return (
    'hashedVkId' in obj &&
    isString(obj.hashedVkId) &&
    'encryptedPersonalData' in obj &&
    isString(obj.encryptedPersonalData) &&
    'hmac' in obj &&
    isString(obj.hmac) &&
    'preferences' in obj &&
    isUserPreferences(obj.preferences)
  );
};

export const isUpdatePersonalDataArgs = (
  obj: unknown
): obj is UserTypes.UpdatePersonalDataArgs => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  return (
    'encryptedPersonalData' in obj &&
    isString(obj.encryptedPersonalData) &&
    'hmac' in obj &&
    isString(obj.hmac) &&
    ('preferences' in obj
      ? obj.preferences === undefined || isUserPreferences(obj.preferences)
      : true)
  );
};

export const isBranchForSubscription = (
  obj: unknown
): obj is BranchForSubscription => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  return (
    'id' in obj &&
    isNumber(obj.id) &&
    'name' in obj &&
    isString(obj.name) &&
    'slug' in obj &&
    isString(obj.slug) &&
    'price' in obj &&
    isNumber(obj.price)
  );
};

export const isBranchForSubscriptionArray = (
  obj: unknown
): obj is BranchForSubscription[] => {
  return Array.isArray(obj) && obj.every(item => isBranchForSubscription(item));
};

export const isAllInSubscriptionTableData = (
  obj: unknown
): obj is AppTypes.AllInSubscriptionTableData => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  return (
    'key' in obj &&
    isNumber(obj.key) &&
    'branchName' in obj &&
    isString(obj.branchName)
  );
};

export const isCustomSubscriptionTableData = (
  obj: unknown
): obj is AppTypes.CustomSubscriptionTableData => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }
  if (!isAllInSubscriptionTableData) return false;
  return (
    'price' in obj &&
    isNumber(obj.price) &&
    'label' in obj &&
    isString(obj.label)
  );
};

export const isOpenGraphImageDB = (
  obj: unknown
): obj is AppTypes.OpenGraphImageDB => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }
  return (
    'url' in obj &&
    isString(obj.url) &&
    'width' in obj &&
    isString(obj.width) &&
    'height' in obj &&
    isString(obj.height) &&
    'alt' in obj &&
    isString(obj.alt)
  );
};

export const isOpenGraphImageDBArray = (
  obj: unknown
): obj is AppTypes.OpenGraphImageDB[] => {
  return Array.isArray(obj) && obj.every(item => isOpenGraphImageDB(item));
};

export const isOpenGraphDB = (obj: unknown): obj is AppTypes.OpenGraphDB => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }
  return 'images' in obj && isOpenGraphImageDBArray(obj.images);
};

export const isOffer = (obj: unknown): obj is Offer => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }
  const offer = obj as {
    '@type'?: unknown;
    name?: unknown;
    priceCurrency?: unknown;
    price?: unknown;
    availability?: unknown;
    url?: unknown;
  };
  return (
    offer['@type'] === 'Offer' &&
    'name ' in offer &&
    isString(offer.name) &&
    'priceCurrency' in offer &&
    isString(offer.priceCurrency) &&
    'price' in offer &&
    isString(offer.price) &&
    'availability' in offer &&
    isString(offer.availability) &&
    'url' in offer &&
    isString(offer.url)
  );
};

export const isOffersArray = (obj: unknown): obj is Offer[] => {
  return Array.isArray(obj) && obj.every(item => isOffer(item));
};

export const isMetadataClient = (
  obj: unknown
): obj is AppTypes.MetadataClient => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }
  return (
    'pageSlug' in obj &&
    isString(obj.pageSlug) &&
    'title' in obj &&
    isString(obj.title) &&
    'description' in obj &&
    isString(obj.description) &&
    'openGraph' in obj &&
    isOpenGraph(obj.openGraph) &&
    (!('offer' in obj) || // offer может отсутствовать
      isOffersArray(obj.offer)) // или должен быть массивом
  );
};

export const isOpenGraphImage = (
  obj: unknown
): obj is AppTypes.OpenGraphImage => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }
  return 'url' in obj && isString(obj.url);
};

export const isOpenGraphImagesArray = (
  obj: unknown
): obj is AppTypes.OpenGraphImage[] => {
  return Array.isArray(obj) && obj.every(item => isOpenGraphImage(item));
};

export const isOpenGraph = (obj: unknown): obj is OpenGraph => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }
  return (
    'type' in obj &&
    isString(obj.type) &&
    'url' in obj &&
    isString(obj.url) &&
    'title' in obj &&
    isString(obj.title) &&
    'description' in obj &&
    isString(obj.description) &&
    'siteName' in obj &&
    isString(obj.siteName) &&
    'images' in obj &&
    isOpenGraphImagesArray(obj.images)
  );
};

export const isSubscriptionForSchema = (
  obj: unknown
): obj is SubscriptionForSchema => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }
  return (
    'name' in obj &&
    isString(obj.name) &&
    'price' in obj &&
    isNumber(obj.price) &&
    'minPrice' in obj &&
    isNumber(obj.price) &&
    'maxPrice' in obj &&
    isNumber(obj.price) &&
    'active' in obj &&
    isBoolean(obj.active)
  );
};

export const isSubscriptionForSchemaArray = (
  obj: unknown
): obj is SubscriptionForSchema[] => {
  return Array.isArray(obj) && obj.every(item => isSubscriptionForSchema(item));
};

export const isSelectedFieldsForNavigaton = (
  obj: unknown
): obj is AppTypes.SelectedFieldsForNavigaton => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }
  return (
    'name' in obj && isString(obj.name) && 'slug' in obj && isString(obj.slug)
  );
};

export const isSelectedFieldsForNavigatonArray = (
  obj: unknown
): obj is AppTypes.SelectedFieldsForNavigaton[] => {
  return (
    Array.isArray(obj) && obj.every(item => isSelectedFieldsForNavigaton(item))
  );
};

export const isCases = (obj: unknown): obj is AppTypes.Cases => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }
  return (
    'nominative' in obj &&
    isString(obj.nominative) &&
    'genitive' in obj &&
    isString(obj.genitive) &&
    'dative' in obj &&
    isString(obj.dative) &&
    'accusative' in obj &&
    isString(obj.accusative) &&
    'instrumental' in obj &&
    isString(obj.instrumental) &&
    'prepositional' in obj &&
    isString(obj.prepositional)
  );
};

export const isBranchWithParsedCases = (
  obj: unknown
): obj is BranchWithParsedCases => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  return (
    'slug' in obj &&
    isString(obj.slug) &&
    'name' in obj &&
    isString(obj.name) &&
    'cases' in obj &&
    isCases(obj.cases) &&
    'topics' in obj &&
    isSelectedFieldsForNavigatonArray(obj.topics)
  );
};

export const isBranchWithParsedCasesArray = (
  obj: unknown
): obj is BranchWithParsedCases[] => {
  return Array.isArray(obj) && obj.every(item => isBranchWithParsedCases(item));
};

export function isConnectUser(obj: unknown): obj is AppTypes.ConnectUser {
  if (typeof obj !== 'object' || obj === null) return false;

  return (
    'connect' in obj &&
    typeof obj.connect === 'object' &&
    obj.connect !== null &&
    'id' in obj.connect &&
    isString(obj.connect.id)
  );
}

export const isPaymentCreateInput = (
  obj: unknown
): obj is Prisma.PaymentCreateInput => {
  if (typeof obj !== 'object' || obj === null) return false;

  const asserted = obj as Record<string, unknown>;

  // Обязательные поля
  if (
    !('yookassaPaymentId' in asserted) ||
    !isString(asserted.yookassaPaymentId)
  ) {
    return false;
  }
  if (!('user' in asserted) || !isConnectUser(asserted.user)) {
    return false;
  }
  if (!('amount' in asserted) || !isNumber(asserted.amount)) {
    return false;
  }

  // Опциональные поля с проверкой
  if (
    'currency' in asserted &&
    asserted.currency !== undefined &&
    !isCurrency(asserted.currency)
  ) {
    return false;
  }
  if (
    'status' in asserted &&
    asserted.status !== undefined &&
    !isPaymentStatus(asserted.status)
  ) {
    return false;
  }
  if (
    'action' in asserted &&
    asserted.action !== undefined &&
    !isPaymentAction(asserted.action)
  ) {
    return false;
  }
  if ('createdAt' in asserted && asserted.createdAt !== undefined) {
    if (!isISOString(asserted.createdAt) && !isValidDate(asserted.createdAt)) {
      return false;
    }
  }
  if ('updatedAt' in asserted && asserted.updatedAt !== undefined) {
    if (!isISOString(asserted.updatedAt) && !isValidDate(asserted.updatedAt)) {
      return false;
    }
  }

  return true;
};

export function isPaymentEvent(event: YooKassaEvent): event is PaymentEvent {
  return event.event.startsWith('payment.');
}

export function isRefundEvent(event: YooKassaEvent): event is RefundEvent {
  return event.event === 'refund.succeeded';
}

// Более конкретные type guards для каждого типа платежного события
export function isPaymentSucceeded(
  event: YooKassaEvent
): event is Extract<YooKassaEvent, { event: 'payment.succeeded' }> {
  return event.event === 'payment.succeeded';
}

export function isPaymentWaitingForCapture(
  event: YooKassaEvent
): event is Extract<YooKassaEvent, { event: 'payment.waiting_for_capture' }> {
  return event.event === 'payment.waiting_for_capture';
}

export function isPaymentCanceled(
  event: YooKassaEvent
): event is Extract<YooKassaEvent, { event: 'payment.canceled' }> {
  return event.event === 'payment.canceled';
}

// Type guard для проверки metadata с userId
export function hasUserIdMetadata(object: {
  metadata: unknown;
}): object is { metadata: { userId: string } } {
  return (
    typeof object === 'object' &&
    object !== null &&
    'metadata' in object &&
    typeof object.metadata === 'object' &&
    object.metadata !== null &&
    'userId' in object.metadata
  );
}

export const isAmount = (obj: unknown): obj is AppTypes.Amount => {
  if (typeof obj !== 'object' || obj === null) return false;

  return (
    'value' in obj &&
    isString(obj.value) &&
    'currency' in obj &&
    isCurrency(obj.currency)
  );
};

export const isPaymentRedisRecordData = (
  obj: unknown
): obj is PaymentRedisRecordData => {
  if (typeof obj !== 'object' || obj === null) return false;

  return (
    'paymentId' in obj &&
    isString(obj.paymentId) &&
    'userId' in obj &&
    isString(obj.userId) &&
    'amount' in obj &&
    isAmount(obj.amount) &&
    'status' in obj &&
    isPaymentStatus(obj.status) &&
    'createdAt' in obj &&
    isISOString(obj.createdAt) &&
    'action' in obj &&
    isPaymentAction(obj.action) &&
    'activatedVia' in obj &&
    isActivationSource(obj.activatedVia)
  );
};

export const isPaymentRedisRecordDatasArray = (
  obj: unknown
): obj is PaymentRedisRecordData[] => {
  return (
    Array.isArray(obj) && obj.every(item => isPaymentRedisRecordData(item))
  );
};

export const isPersonalDataForReceipt = (
  obj: unknown
): obj is PersonalDataForReceipt => {
  if (typeof obj !== 'object' || obj === null) return false;

  return 'email' in obj && isString(obj.email);
};

export const isUsedPromoCodeInput = (
  obj: unknown
): obj is UsedPromoCodeInput => {
  if (typeof obj !== 'object' || obj === null) return false;

  return (
    'usedCount' in obj &&
    isNumber(obj.usedCount) &&
    'isActive' in obj &&
    isBoolean(obj.isActive)
  );
};
